import { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { getPossibleParentBoundaries } from '@/lib/boundaryHierarchy';
import type { Boundary } from '@/types/diagram';

export const BOUNDARY_NAME_MAX_LENGTH = 500;
export const BOUNDARY_DESCRIPTION_MAX_LENGTH = 50000;
const ROOT_VALUE = 'root';

export interface BoundaryEdit {
  name: string;
  description: string;
  parent_boundary_id: number | null;
}

interface EditBoundaryDialogProps {
  boundary: Boundary;
  boundaries: Boundary[];
  isDraft: boolean;
  isSaving: boolean;
  saveError: string | null;
  saveSuccess: boolean;
  onCancel: () => void;
  onSave: (edit: BoundaryEdit) => void;
}

export default function EditBoundaryDialog({
  boundary,
  boundaries,
  isDraft,
  isSaving,
  saveError,
  saveSuccess,
  onCancel,
  onSave,
}: EditBoundaryDialogProps) {
  const [name, setName] = useState(boundary.name ?? '');
  const [description, setDescription] = useState(boundary.description ?? '');
  const [parentValue, setParentValue] = useState(
    boundary.parent_boundary_id != null ? String(boundary.parent_boundary_id) : ROOT_VALUE,
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  const parentOptions = useMemo(
    () => getPossibleParentBoundaries(boundaries, boundary.id),
    [boundaries, boundary.id],
  );

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setValidationError('Name is required');
      return;
    }
    if (trimmedName.length > BOUNDARY_NAME_MAX_LENGTH) {
      setValidationError(`Name cannot exceed ${BOUNDARY_NAME_MAX_LENGTH} characters`);
      return;
    }
    if (description.length > BOUNDARY_DESCRIPTION_MAX_LENGTH) {
      setValidationError(`Description cannot exceed ${BOUNDARY_DESCRIPTION_MAX_LENGTH} characters`);
      return;
    }
    const parentId = parentValue === ROOT_VALUE ? null : Number(parentValue);
    if (parentId !== null && !parentOptions.some((b) => b.id === parentId)) {
      setValidationError('Selected parent boundary is not valid');
      return;
    }
    setValidationError(null);
    onSave({ name: trimmedName, description, parent_boundary_id: parentId });
  };

  const error = validationError ?? saveError;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Boundary</DialogTitle>
          <DialogDescription>{`${boundary.name} (ID: ${boundary.id})`}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <label htmlFor="edit-boundary-name" className="text-xs font-medium text-muted-foreground">Name</label>
            <Input
              id="edit-boundary-name"
              value={name}
              maxLength={BOUNDARY_NAME_MAX_LENGTH}
              onChange={(e) => setName(e.target.value)}
              placeholder="Boundary name…"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="edit-boundary-description" className="text-xs font-medium text-muted-foreground">Description</label>
            <Textarea
              id="edit-boundary-description"
              value={description}
              maxLength={BOUNDARY_DESCRIPTION_MAX_LENGTH}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description…"
              rows={3}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Parent boundary</label>
            <Select value={parentValue} onValueChange={setParentValue}>
              <SelectTrigger aria-label="Parent boundary"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ROOT_VALUE}>None / Root</SelectItem>
                {parentOptions.map((b) => (
                  <SelectItem key={b.id} value={String(b.id)}>{b.name || `Boundary #${b.id}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
          {saveSuccess && (
            <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Saved successfully
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : isDraft ? 'Save Draft' : 'Save & Sync'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
